/**
 * API Endpoints Configuration
 * Centralized endpoint definitions matching the OpenAPI specification
 */

// Base API path
const API_PREFIX = '/api';

// Auth Endpoints
export const AUTH_ENDPOINTS = {
  GOOGLE_LOGIN: `${API_PREFIX}/Auth/google-login`,
  EMAIL_LOGIN: `${API_PREFIX}/Auth/email-login`,
  REGISTER: `${API_PREFIX}/Auth/register`,
  ME: `${API_PREFIX}/Auth/me`,
  REFRESH_TOKEN: `${API_PREFIX}/Auth/refresh-token`,
  REVOKE_TOKEN: `${API_PREFIX}/Auth/revoke-token`,
  FORGOT_PASSWORD: `${API_PREFIX}/Auth/forgot-password`,
  RESET_PASSWORD: `${API_PREFIX}/Auth/reset-password`,
  VERIFY_EMAIL: `${API_PREFIX}/Auth/verify-email`,
  RESEND_VERIFICATION: `${API_PREFIX}/Auth/resend-verification`,
  QR_LOGIN_SESSION: `${API_PREFIX}/Auth/qr-login-session`,
  QR_LOGIN_SESSION_STATUS: (sessionId) => `${API_PREFIX}/Auth/qr-login-session/${sessionId}/status`,
};

// Form Schema Endpoints
export const FORM_SCHEMA_ENDPOINTS = {
  LIST: `${API_PREFIX}/FormSchema`,
  CREATE: `${API_PREFIX}/FormSchema`,
  GET_BY_ID: (id) => `${API_PREFIX}/FormSchema/${id}`,
  UPDATE: (id) => `${API_PREFIX}/FormSchema/${id}`,
  DELETE: (id) => `${API_PREFIX}/FormSchema/${id}`,
  DUPLICATE: (id) => `${API_PREFIX}/FormSchema/${id}/duplicate`,
  PREVIEW: (id) => `${API_PREFIX}/FormSchema/${id}/preview`,
  REPEATED: `${API_PREFIX}/FormSchema/repeated`,
  SUGGESTIONS: (id) => `${API_PREFIX}/FormSchema/${id}/suggestions`,
  SUPPRESS_SAMPLE_ID: (id) => `${API_PREFIX}/FormSchema/${id}/suppress-sample-id-suggestion`,
};

// Form Submission Endpoints
export const FORM_SUBMISSION_ENDPOINTS = {
  LIST: `${API_PREFIX}/FormSubmission`,
  CREATE: `${API_PREFIX}/FormSubmission`,
  GET_BY_ID: (id) => `${API_PREFIX}/FormSubmission/${id}`,
  UPDATE_DATA: (id) => `${API_PREFIX}/FormSubmission/${id}`,
  UPDATE_STATUS: (id) => `${API_PREFIX}/FormSubmission/${id}/status`,
  APPROVE: (id) => `${API_PREFIX}/FormSubmission/${id}/approve`,
  REJECT: (id) => `${API_PREFIX}/FormSubmission/${id}/reject`,
  CHILDREN: (id) => `${API_PREFIX}/FormSubmission/${id}/children`,
  LINK_CHILD: (parentId) => `${API_PREFIX}/FormSubmission/${parentId}/children/link`,
  UNLINK_CHILD: (parentId, childId) => `${API_PREFIX}/FormSubmission/${parentId}/children/${childId}/link`,
  DELETE: (id) => `${API_PREFIX}/FormSubmission/${id}`,
  DELETE_IMPACT: (id) => `${API_PREFIX}/FormSubmission/${id}/delete-impact`,
  BULK_DELETE: `${API_PREFIX}/FormSubmission/bulk`,
  ACTIVATE: (id) => `${API_PREFIX}/FormSubmission/${id}/activate`,
  ASSIGN_WORK_ORDER: (id) => `${API_PREFIX}/FormSubmission/${id}/work-order`,
  COMMENTS: (id) => `${API_PREFIX}/FormSubmission/${id}/comments`,
  DELETE_COMMENT: (commentId) => `${API_PREFIX}/FormSubmission/comments/${commentId}`,
  UNREAD_COMMENT_COUNTS: `${API_PREFIX}/FormSubmission/unread-comment-counts`,
};

// Submission Analytics Endpoints
export const SUBMISSION_ANALYTICS_ENDPOINTS = {
  AGGREGATE: (formSchemaId) => `${API_PREFIX}/SubmissionAnalytics/${formSchemaId}/aggregate`,
  SUMMARY: (formSchemaId) => `${API_PREFIX}/SubmissionAnalytics/${formSchemaId}/summary`,
  FILTER: (formSchemaId) => `${API_PREFIX}/SubmissionAnalytics/${formSchemaId}/filter`,
  GROUPBY: (formSchemaId) => `${API_PREFIX}/SubmissionAnalytics/${formSchemaId}/groupby`,
  SCATTER: (formSchemaId) => `${API_PREFIX}/SubmissionAnalytics/${formSchemaId}/scatter`,
};

// Dashboard Endpoints
export const DASHBOARD_ENDPOINTS = {
  SUMMARY: `${API_PREFIX}/Dashboard/summary`,
  ANALYTICS: `${API_PREFIX}/Dashboard/analytics`,
  UPCOMING_WORK_ORDERS: `${API_PREFIX}/Dashboard/upcoming-work-orders`,
};

// FAQ Endpoints
export const FAQ_ENDPOINTS = {
  PUBLIC: `${API_PREFIX}/Faq/public`,
  PUBLIC_BY_CATEGORY: (category) => `${API_PREFIX}/Faq/public/category/${category}`,
  LIST: `${API_PREFIX}/Faq`,
  GET_BY_ID: (id) => `${API_PREFIX}/Faq/${id}`,
  CREATE: `${API_PREFIX}/Faq`,
  UPDATE: (id) => `${API_PREFIX}/Faq/${id}`,
  DELETE: (id) => `${API_PREFIX}/Faq/${id}`,
  REORDER: `${API_PREFIX}/Faq/reorder`,
  GENERATE_ANSWER: `${API_PREFIX}/Faq/ai/generate-answer`,
  POLISH_ANSWER: `${API_PREFIX}/Faq/ai/polish-answer`,
};

// User Endpoints
export const USER_ENDPOINTS = {
  PROFILE: `${API_PREFIX}/User/profile`,
  UPDATE_PROFILE: `${API_PREFIX}/User/profile`,
  UPLOAD_AVATAR: `${API_PREFIX}/User/profile/avatar`,
  REMOVE_AVATAR: `${API_PREFIX}/User/profile/avatar`,
  STATISTICS: `${API_PREFIX}/User/statistics`,
  CHANGE_PASSWORD: `${API_PREFIX}/User/profile/change-password`,
  LIST: `${API_PREFIX}/User`,
  CREATE: `${API_PREFIX}/User`,
  GET_BY_ID: (id) => `${API_PREFIX}/User/${id}`,
  UPDATE: (id) => `${API_PREFIX}/User/${id}`,
  DELETE: (id) => `${API_PREFIX}/User/${id}`,
  UPDATE_ROLE: (id) => `${API_PREFIX}/User/${id}/role`,
  ACTIVATE: (id) => `${API_PREFIX}/User/${id}/activate`,
  DEACTIVATE: (id) => `${API_PREFIX}/User/${id}/deactivate`,
};

// Organization Endpoints
export const ORGANIZATION_ENDPOINTS = {
  LIST: `${API_PREFIX}/Organization`,
  CREATE: `${API_PREFIX}/Organization`,
  GET_BY_ID: (id) => `${API_PREFIX}/Organization/${id}`,
  UPDATE: (id) => `${API_PREFIX}/Organization/${id}`,
  DELETE: (id) => `${API_PREFIX}/Organization/${id}`,
  ACTIVATE: (id) => `${API_PREFIX}/Organization/${id}/activate`,
  DEACTIVATE: (id) => `${API_PREFIX}/Organization/${id}/deactivate`,
  // Demo Studio analytics (SuperAdmin only): per-demo view/launch/login counts.
  DEMO_STATS_SUMMARY: `${API_PREFIX}/organization/demos/stats-summary`,
  DEMO_STATS: (orgId, days = 30) => `${API_PREFIX}/organization/demos/${orgId}/stats?days=${days}`,
  // Demo credentials + subdomain provisioning (SuperAdmin only, TASK-0264).
  DEMO_CREDENTIALS: (id) => `${API_PREFIX}/Organization/${id}/demo-credentials`,
  DEMO_CREDENTIALS_RESET: (id) => `${API_PREFIX}/Organization/${id}/demo-credentials/reset-builder`,
  DEMO_DOMAIN: (id) => `${API_PREFIX}/Organization/${id}/demo-domain`,
};

// Permission Endpoints
export const PERMISSION_ENDPOINTS = {
  ASSIGN: `${API_PREFIX}/Permission/assign`,
  REVOKE: `${API_PREFIX}/Permission/revoke`,
  GET_USER_PERMISSIONS: (userId) => `${API_PREFIX}/Permission/user/${userId}`,
  GET_FORM_PERMISSIONS: (formSchemaId) => `${API_PREFIX}/Permission/form/${formSchemaId}`,
  GET_USER_FORM_PERMISSIONS: (userId, formSchemaId) => `${API_PREFIX}/Permission/user/${userId}/form/${formSchemaId}`,
  CHECK: `${API_PREFIX}/Permission/check`,
  MY_PERMISSIONS: (formSchemaId) => `${API_PREFIX}/Permission/my-permissions/${formSchemaId}`,
};

// Form Submission Query Endpoints
export const FORM_SUBMISSION_QUERY_ENDPOINTS = {
  FIELDS: (submissionId) => `${API_PREFIX}/FormSubmissionQuery/fields/${submissionId}`,
  EXPORT_CSV: `${API_PREFIX}/FormSubmissionQuery/export/csv`,
  EXPORT_EXCEL: `${API_PREFIX}/FormSubmissionQuery/export/excel`,
  SUGGESTIONS: `${API_PREFIX}/FormSubmissionQuery/suggestions`,
};

// Excel Import Endpoints (admin-only; inspect → map → analyze → review → commit → poll)
export const EXCEL_IMPORT_ENDPOINTS = {
  TEMPLATE: (formSchemaId) => `${API_PREFIX}/ExcelImport/template?formSchemaId=${formSchemaId}`,
  INSPECT: `${API_PREFIX}/ExcelImport/inspect`,
  ANALYZE: `${API_PREFIX}/ExcelImport/analyze`,
  COMMIT: (jobId) => `${API_PREFIX}/ExcelImport/jobs/${jobId}/commit`,
  JOB: (jobId) => `${API_PREFIX}/ExcelImport/jobs/${jobId}`,
  JOBS: (formSchemaId) => `${API_PREFIX}/ExcelImport/jobs?formSchemaId=${formSchemaId}`,
};

// Demo Data Endpoints (demo-org builders only; AI-generate realistic dummy data: preview → commit).
// entityType: 'forms' (targetId = form schema id) | 'workorders' | 'customers' (targetId = demo org id).
export const DEMO_DATA_ENDPOINTS = {
  PREVIEW: (entityType, targetId) => `${API_PREFIX}/demodata/${entityType}/${targetId}/preview`,
  COMMIT: (entityType, targetId) => `${API_PREFIX}/demodata/${entityType}/${targetId}/commit`,
  // AI scaffold (SuperAdmin): plan N form briefs from one prompt → create each form → fill all forms.
  SCAFFOLD_PLAN: (orgId) => `${API_PREFIX}/demodata/orgs/${orgId}/scaffold-plan`,
  SCAFFOLD_FORM: (orgId) => `${API_PREFIX}/demodata/orgs/${orgId}/scaffold-form`,
  ORG_FORMS: (orgId) => `${API_PREFIX}/demodata/orgs/${orgId}/forms`,
  DELETE_ORG: (orgId) => `${API_PREFIX}/demodata/orgs/${orgId}`, // hard teardown of a demo org (SuperAdmin, TASK-0272)
  SUGGEST_CONFIG: `${API_PREFIX}/demodata/demo-config`, // AI-fill the New Demo config form (name/slug/landing); no org

  ENSURE_AVATAR_POOL: `${API_PREFIX}/demodata/avatar-pool/ensure`, // ?append=true generates + appends a batch
  AVATAR_POOL_GENERATE: `${API_PREFIX}/demodata/avatar-pool/generate`, // body { prompt, count, model }
  AVATAR_POOL_STATUS: `${API_PREFIX}/demodata/avatar-pool/status`,
  AVATAR_POOL_LIST: `${API_PREFIX}/demodata/avatar-pool/avatars`,
  AVATAR_POOL_ADD: `${API_PREFIX}/demodata/avatar-pool/avatars`,
  AVATAR_POOL_DELETE: (name) => `${API_PREFIX}/demodata/avatar-pool/avatars/${encodeURIComponent(name)}`,
};

// AI Form Schema Endpoints
export const AI_FORM_SCHEMA_ENDPOINTS = {
  GENERATE: `${API_PREFIX}/AIFormSchema/generate`,
  GENERATE_STREAM: `${API_PREFIX}/AIFormSchema/generate-stream`,
  SUGGEST_FIELDS: `${API_PREFIX}/AIFormSchema/suggest-fields`,
  REFINE: `${API_PREFIX}/AIFormSchema/refine`,
};

// AI PDF Template Endpoints
export const AI_PDF_TEMPLATE_ENDPOINTS = {
  GENERATE: `${API_PREFIX}/AIPdfTemplate/generate`,
  GENERATE_STREAM: `${API_PREFIX}/AIPdfTemplate/generate-stream`,
};

// Storage Endpoints
export const STORAGE_ENDPOINTS = {
  SAS_TOKEN: `${API_PREFIX}/Storage/sas-token`,
  SAS_TOKEN_UPLOAD: `${API_PREFIX}/Storage/sas-token/upload`,
  SAS_TOKEN_MANAGER: `${API_PREFIX}/Storage/sas-token/manager`,
  CONFIG: `${API_PREFIX}/Storage/config`,
  TEMP_PATH: `${API_PREFIX}/Storage/temp-path`,
  MOVE_FILE: `${API_PREFIX}/Storage/move-file`,
  DELETE_TEMP_FILE: `${API_PREFIX}/Storage/temp-file`,
  FILE_URL: `${API_PREFIX}/Storage/file-url`,
  FILE_INFO: `${API_PREFIX}/Storage/file-info`,
  CLEANUP_TEMP: `${API_PREFIX}/Storage/cleanup-temp`,
  VALIDATE_FILE: `${API_PREFIX}/Storage/validate-file`,
  USAGE: `${API_PREFIX}/Storage/usage`,
  FILES: `${API_PREFIX}/Storage/files`,
  FILE_METADATA: `${API_PREFIX}/Storage/files/metadata`,
  DELETE_FILE: `${API_PREFIX}/Storage/files`,
  RECONCILE: (orgId) => `${API_PREFIX}/Storage/reconcile?organizationId=${orgId}`,
};

// PDF Template Endpoints
export const PDF_TEMPLATE_ENDPOINTS = {
  LIST_BY_FORM: (formSchemaId) => `${API_PREFIX}/PdfTemplate/schema/${formSchemaId}`,
  GET_BY_ID: (id) => `${API_PREFIX}/PdfTemplate/${id}`,
  CREATE: `${API_PREFIX}/PdfTemplate`,
  UPDATE: (id) => `${API_PREFIX}/PdfTemplate/${id}`,
  DELETE: (id) => `${API_PREFIX}/PdfTemplate/${id}`,
  DELETE_PERMANENT: (id) => `${API_PREFIX}/PdfTemplate/${id}/permanent`,
  SET_DEFAULT: (id) => `${API_PREFIX}/PdfTemplate/${id}/set-default`,
  DUPLICATE: (id) => `${API_PREFIX}/PdfTemplate/${id}/duplicate`,
  GET_DEFAULT: (formSchemaId) => `${API_PREFIX}/PdfTemplate/schema/${formSchemaId}/default`,
};

// Standalone (Preact + htm) visualization share links — NEW parallel viz track.
// The public viewer/data routes (/v/{token}, /v/{token}/data) are anonymous and the public URL is
// returned by the API in the share DTO; only the authenticated management routes live here.
export const STANDALONE_VIZ_ENDPOINTS = {
  LIST_SHARES: (submissionId) => `${API_PREFIX}/standalone-viz/submissions/${submissionId}/shares`,
  CREATE_SHARE: (submissionId) => `${API_PREFIX}/standalone-viz/submissions/${submissionId}/shares`,
  REVOKE_SHARE: (shareId) => `${API_PREFIX}/standalone-viz/shares/${shareId}`,
  // Authenticated, self-contained render of ONE submission as a standalone HTML doc (payload inlined).
  // vizId optional → the server uses the form's default visualization.
  RENDER_DOC: (submissionId, vizId) =>
    `${API_PREFIX}/standalone-viz/submissions/${submissionId}/render${vizId ? `?viz=${vizId}` : ''}`,
  // AI-authored prompt suggestions for a form's first-time visualization generation. Optional
  // sampleSubmissionId grounds the ideas in a specific real submission (else the server auto-picks one);
  // optional type ('visualization' | 'card' | 'list') tailors the ideas to a full report, a compact card,
  // or an aggregate list dashboard.
  SUGGESTIONS: (formSchemaId, sampleSubmissionId, type) => {
    const qs = new URLSearchParams();
    if (sampleSubmissionId) qs.set('sampleSubmissionId', String(sampleSubmissionId));
    if (type) qs.set('type', type === 'card' ? 'Card' : type === 'list' ? 'ListView' : 'Visualization');
    const q = qs.toString();
    return `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/suggestions${q ? `?${q}` : ''}`;
  },
  // Per-form AI design authoring (the reusable view.js)
  GENERATE_DESIGN_STREAM: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design/generate-stream`,
  REFINE_DESIGN_STREAM: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design/refine-stream`,
  GET_DESIGN: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design`,
  SAVE_DESIGN: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design`,
  SAVE_DESIGN_FILE: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design/file`,
  DELETE_DESIGN: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/design`,

  // Multi-visualization dashboard (NEW): each form schema can own several named visualizations,
  // one of which is the default. Each visualization carries its own vizId-scoped design mini-project.
  LIST_VIZ: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations`,
  CREATE_VIZ: (formSchemaId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations`,
  UPDATE_VIZ: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}`,
  DELETE_VIZ: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}`,
  SET_DEFAULT_VIZ: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/set-default`,
  // Card-only: toggle whether the card renders inline in the submission sidebar (per-form admin control).
  SET_SHOW_IN_SUBMISSION: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/show-in-submission`,
  // List visualizations (ListView): saved data-source / filter / limit config + render (saved GET, override POST).
  LIST_CONFIG_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/list-config`,
  RENDER_LIST_DOC: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/render-list`,
  GET_DESIGN_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design`,
  SAVE_DESIGN_FILE_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design/file`,
  DELETE_DESIGN_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design`,
  GENERATE_DESIGN_STREAM_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design/generate-stream`,
  REFINE_DESIGN_STREAM_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design/refine-stream`,
  // Pre-generation triage: routes a message to refine vs generate + up to 3 clarifying questions (fail-soft).
  PREFLIGHT_DESIGN_V: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/design/preflight`,
  // Per-viz uploaded image assets (private; inlined as data-URIs at render via d.asset('name')).
  LIST_VIZ_IMAGES: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images`,
  REGISTER_VIZ_IMAGE: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images`,
  DELETE_VIZ_IMAGE: (formSchemaId, vizId, name) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images/${encodeURIComponent(name)}`,
  // Hand-edit an image's AI description (name in body) / re-run the vision model (name in body) — both avoid the
  // catch-all delete route.
  UPDATE_VIZ_IMAGE: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images`,
  ANALYZE_VIZ_IMAGE: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images/analyze`,
  // AI image generation: queue a background job (the produced image lands in this viz's image store) + poll it.
  GENERATE_VIZ_IMAGE: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images/generate`,
  GENERATE_VIZ_IMAGE_JOB: (formSchemaId, vizId, jobId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images/generate/${jobId}`,
  GENERATE_VIZ_IMAGE_PRESETS: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/images/presets`,
  // ListView public share links (the aggregate counterpart of CREATE_SHARE/LIST_SHARES/REVOKE_SHARE). Create
  // captures the effective list config; the public URL (/v/list/{token}) rides in the returned DTO.
  LIST_VIZ_SHARES: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/shares`,
  REVOKE_LIST_VIZ_SHARE: (shareId) => `${API_PREFIX}/standalone-viz/list-shares/${shareId}`,
  // Per-viz design version history (snapshots tagged with the prompt; restore an earlier good state).
  LIST_VIZ_VERSIONS: (formSchemaId, vizId) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/versions`,
  RESTORE_VIZ_VERSION: (formSchemaId, vizId, versionNumber) => `${API_PREFIX}/standalone-viz/forms/${formSchemaId}/visualizations/${vizId}/versions/${versionNumber}/restore`,
};

// Demo marketing page (AI-authored, ONE per demo org — resolved server-side, no vizId). SuperAdmin may target
// any demo org, an OrganizationAdmin only their own; the backend enforces this. Mirrors the standalone-viz
// design + image endpoints, keyed by demo org id.
export const DEMO_PAGE_ENDPOINTS = {
  GET_PAGE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}`,
  GET_DESIGN: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/design`,
  GENERATE_STREAM: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/generate-stream`,
  REFINE_STREAM: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/refine-stream`,
  RENDER: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/render`,
  LIST_IMAGES: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images`,
  REGISTER_IMAGE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images`,
  DELETE_IMAGE: (orgId, name) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images/${encodeURIComponent(name)}`,
  UPDATE_IMAGE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images`,
  ANALYZE_IMAGE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images/analyze`,
  PREFLIGHT: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/preflight`,
  SUGGESTIONS: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/suggestions`,
  SAVE_DESIGN_FILE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/design/file`,
  DELETE_DESIGN: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/design`,
  LIST_VERSIONS: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/versions`,
  RESTORE_VERSION: (orgId, n) => `${API_PREFIX}/demo-pages/orgs/${orgId}/versions/${n}/restore`,
  GENERATE_IMAGE: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images/generate`,
  GENERATE_IMAGE_JOB: (orgId, jobId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images/generate/${jobId}`,
  IMAGE_PRESETS: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/images/presets`,
  REGENERATE_SNAPSHOT: (orgId) => `${API_PREFIX}/demo-pages/orgs/${orgId}/regenerate-snapshot`,
};

// Timezone Endpoints
export const TIMEZONE_ENDPOINTS = {
  LIST: `${API_PREFIX}/Timezone`,
  VALIDATE: (timezoneId) => `${API_PREFIX}/Timezone/validate/${timezoneId}`,
};

// Column Configuration Endpoints
export const COLUMN_CONFIGURATION_ENDPOINTS = {
  LIST_BY_FORM: (formSchemaId) => `${API_PREFIX}/ColumnConfiguration/schema/${formSchemaId}`,
  GET_BY_ID: (id) => `${API_PREFIX}/ColumnConfiguration/${id}`,
  GET_DEFAULT: (formSchemaId) => `${API_PREFIX}/ColumnConfiguration/schema/${formSchemaId}/default`,
  CREATE: `${API_PREFIX}/ColumnConfiguration`,
  UPDATE: (id) => `${API_PREFIX}/ColumnConfiguration/${id}`,
  DELETE: (id) => `${API_PREFIX}/ColumnConfiguration/${id}`,
  SET_DEFAULT: (id) => `${API_PREFIX}/ColumnConfiguration/${id}/set-default`,
  TOGGLE_SHARE: (id) => `${API_PREFIX}/ColumnConfiguration/${id}/toggle-share`,
  DUPLICATE: (id) => `${API_PREFIX}/ColumnConfiguration/${id}/duplicate`,
};

// Customer Endpoints
export const CUSTOMER_ENDPOINTS = {
  LIST: `${API_PREFIX}/Customer`,
  CREATE: `${API_PREFIX}/Customer`,
  GET_BY_ID: (id) => `${API_PREFIX}/Customer/${id}`,
  UPDATE: (id) => `${API_PREFIX}/Customer/${id}`,
  DELETE: (id) => `${API_PREFIX}/Customer/${id}`,
  LOCATIONS: (id) => `${API_PREFIX}/Customer/${id}/locations`,
};

// Location Endpoints
export const LOCATION_ENDPOINTS = {
  LIST: `${API_PREFIX}/Location`,
  CREATE: `${API_PREFIX}/Location`,
  GET_BY_ID: (id) => `${API_PREFIX}/Location/${id}`,
  UPDATE: (id) => `${API_PREFIX}/Location/${id}`,
  DELETE: (id) => `${API_PREFIX}/Location/${id}`,
};

// Work Order Endpoints
export const WORK_ORDER_ENDPOINTS = {
  LIST: `${API_PREFIX}/WorkOrder`,
  CREATE: `${API_PREFIX}/WorkOrder`,
  GET_BY_ID: (id) => `${API_PREFIX}/WorkOrder/${id}`,
  UPDATE: (id) => `${API_PREFIX}/WorkOrder/${id}`,
  UPDATE_STATUS: (id) => `${API_PREFIX}/WorkOrder/${id}/status`,
  DELETE: (id) => `${API_PREFIX}/WorkOrder/${id}`,
  SUBMISSIONS: (id) => `${API_PREFIX}/WorkOrder/${id}/submissions`,
};

// License Plan Endpoints
export const LICENSE_PLAN_ENDPOINTS = {
  LIST: `${API_PREFIX}/LicensePlan`,
  GET_BY_ID: (id) => `${API_PREFIX}/LicensePlan/${id}`,
  CREATE: `${API_PREFIX}/LicensePlan`,
  UPDATE: (id) => `${API_PREFIX}/LicensePlan/${id}`,
  DELETE: (id) => `${API_PREFIX}/LicensePlan/${id}`,
  SET_DEFAULT: (id) => `${API_PREFIX}/LicensePlan/${id}/set-default`,
};

// Organization License Endpoints
export const ORGANIZATION_LICENSE_ENDPOINTS = {
  LIST: `${API_PREFIX}/OrganizationLicense`,
  GET_FOR_ORG: (orgId) => `${API_PREFIX}/OrganizationLicense/organization/${orgId}`,
  MY_LICENSE: `${API_PREFIX}/OrganizationLicense/my-license`,
  ASSIGN: `${API_PREFIX}/OrganizationLicense`,
  UPDATE: (id) => `${API_PREFIX}/OrganizationLicense/${id}`,
  SUSPEND: (id) => `${API_PREFIX}/OrganizationLicense/${id}/suspend`,
  REACTIVATE: (id) => `${API_PREFIX}/OrganizationLicense/${id}/reactivate`,
  EXTEND: (id) => `${API_PREFIX}/OrganizationLicense/${id}/extend`,
};

// Email Template Endpoints
export const EMAIL_TEMPLATE_ENDPOINTS = {
  LIST: `${API_PREFIX}/EmailTemplate`,
  GET_BY_ID: (id) => `${API_PREFIX}/EmailTemplate/${id}`,
  UPDATE: (id) => `${API_PREFIX}/EmailTemplate/${id}`,
  VARIABLE_INFO: (id) => `${API_PREFIX}/EmailTemplate/${id}/variables`,
  LOGS: `${API_PREFIX}/EmailTemplate/logs`,
  SEND_TEST: (id) => `${API_PREFIX}/EmailTemplate/${id}/test`,
  CREATE_SES_TEMPLATE: (id) => `${API_PREFIX}/EmailTemplate/${id}/create-ses-template`,
  SES_CONTENT: (id) => `${API_PREFIX}/EmailTemplate/${id}/ses-content`,
  GENERATE_AI: `${API_PREFIX}/EmailTemplate/generate-ai`,
  GENERATE_REFERENCE_HTML: `${API_PREFIX}/EmailTemplate/generate-reference-html`,
  BATCH_REGENERATE_STREAM: `${API_PREFIX}/EmailTemplate/batch-regenerate-stream`,
  BRANDING_CONFIG: `${API_PREFIX}/EmailTemplate/branding-config`,
};

// Email Asset Endpoints
export const EMAIL_ASSET_ENDPOINTS = {
  LIST: `${API_PREFIX}/EmailAsset`,
  UPLOAD_TOKEN: `${API_PREFIX}/EmailAsset/upload-token`,
  REGISTER: `${API_PREFIX}/EmailAsset`,
  DELETE: (id) => `${API_PREFIX}/EmailAsset/${id}`,
  URL: (id) => `${API_PREFIX}/EmailAsset/${id}/url`,
};

// Notification Event Endpoints
export const NOTIFICATION_EVENT_ENDPOINTS = {
  LIST: `${API_PREFIX}/NotificationEvent`,
  GET_BY_EVENT: (eventType) => `${API_PREFIX}/NotificationEvent/${eventType}`,
  UPDATE: (eventType) => `${API_PREFIX}/NotificationEvent/${eventType}`,
  RESET: (eventType) => `${API_PREFIX}/NotificationEvent/${eventType}/rules`,
};

// SMS Template Endpoints
export const SMS_TEMPLATE_ENDPOINTS = {
  LIST: `${API_PREFIX}/SmsTemplate`,
  GET_BY_ID: (id) => `${API_PREFIX}/SmsTemplate/${id}`,
  UPDATE: (id) => `${API_PREFIX}/SmsTemplate/${id}`,
  LOGS: `${API_PREFIX}/SmsTemplate/logs`,
  SEND_TEST: (id) => `${API_PREFIX}/SmsTemplate/${id}/test`,
  CONFIG: `${API_PREFIX}/SmsTemplate/config`,
  ORG_OVERRIDE: (systemTemplateId) => `${API_PREFIX}/SmsTemplate/${systemTemplateId}/org-override`,
  DELETE_ORG_OVERRIDE: (id) => `${API_PREFIX}/SmsTemplate/${id}/org-override`,
  GENERATE_AI: `${API_PREFIX}/SmsTemplate/generate-ai`,
  VARIABLE_INFO: (id) => `${API_PREFIX}/SmsTemplate/${id}/variables`,
};

// PDF Report Endpoints
export const PDF_REPORT_ENDPOINTS = {
  LIST: `${API_PREFIX}/PdfReport`,
  FOR_SUBMISSION: (submissionId) => `${API_PREFIX}/PdfReport/submission/${submissionId}`,
  GET_BY_ID: (id) => `${API_PREFIX}/PdfReport/${id}`,
  DOWNLOAD: (id) => `${API_PREFIX}/PdfReport/${id}/download`,
  GENERATE: `${API_PREFIX}/PdfReport/generate`,
  REGENERATE: (id) => `${API_PREFIX}/PdfReport/${id}/regenerate`,
  DELETE: (id) => `${API_PREFIX}/PdfReport/${id}`,
  PUBLIC_URL: (id) => `${API_PREFIX}/PdfReport/${id}/public-url`,
  SHARE: (id) => `${API_PREFIX}/PdfReport/${id}/share`,
  PREVIEW: `${API_PREFIX}/PdfReport/preview`,
};

// Report Settings Endpoints
export const REPORT_SETTINGS_ENDPOINTS = {
  GET: `${API_PREFIX}/ReportSettings`,
  UPDATE: `${API_PREFIX}/ReportSettings`,
};

// System Status Endpoints
export const SYSTEM_STATUS_ENDPOINTS = {
  SERVER: `${API_PREFIX}/SystemStatus/server`,
  PDF_SERVICE: `${API_PREFIX}/SystemStatus/pdf-service`,
  CONNECTIVITY: `${API_PREFIX}/SystemStatus/connectivity`,
  BACKGROUND_JOBS: `${API_PREFIX}/SystemStatus/background-jobs`,
  PDF_QUEUE: `${API_PREFIX}/SystemStatus/pdf-queue`,
  CONFIGURATION: `${API_PREFIX}/SystemStatus/configuration`,
  RECENT_ERRORS: `${API_PREFIX}/SystemStatus/recent-errors`,
  SENTRY_ISSUES: `${API_PREFIX}/SystemStatus/sentry-issues`,
  SENTRY_ISSUE_DETAILS: (issueId) => `${API_PREFIX}/SystemStatus/sentry-issues/${issueId}`,
  SENTRY_ISSUE_LATEST_EVENT: (issueId) => `${API_PREFIX}/SystemStatus/sentry-issues/${issueId}/latest-event`,
  SENTRY_ISSUE_EVENTS: (issueId) => `${API_PREFIX}/SystemStatus/sentry-issues/${issueId}/events`,
  SENTRY_ISSUE_UPDATE_STATUS: (issueId) => `${API_PREFIX}/SystemStatus/sentry-issues/${issueId}/status`,
  MCP_STATUS: `${API_PREFIX}/SystemStatus/mcp`,
};

// Support Task Endpoints
export const SUPPORT_TASK_ENDPOINTS = {
  LIST: `${API_PREFIX}/SupportTask`,
  CREATE: `${API_PREFIX}/SupportTask`,
  GET_BY_ID: (id) => `${API_PREFIX}/SupportTask/${id}`,
  UPDATE: (id) => `${API_PREFIX}/SupportTask/${id}`,
  DELETE: (id) => `${API_PREFIX}/SupportTask/${id}`,
  UPDATE_STATUS: (id) => `${API_PREFIX}/SupportTask/${id}/status`,
  ASSIGNABLE_USERS: `${API_PREFIX}/SupportTask/assignable-users`,
  COMMENTS: (id) => `${API_PREFIX}/SupportTask/${id}/comments`,
  ADD_COMMENT: (id) => `${API_PREFIX}/SupportTask/${id}/comments`,
  DELETE_COMMENT: (commentId) => `${API_PREFIX}/SupportTask/comments/${commentId}`,
  ADD_ATTACHMENT: (id) => `${API_PREFIX}/SupportTask/${id}/attachments`,
  DELETE_ATTACHMENT: (attachmentId) => `${API_PREFIX}/SupportTask/attachments/${attachmentId}`,
  ANALYZE_SENTRY: (issueId) => `${API_PREFIX}/SupportTask/analyze-sentry/${issueId}`,
  RELATIONS: (id) => `${API_PREFIX}/SupportTask/${id}/relations`,
  REMOVE_RELATION: (relationId) => `${API_PREFIX}/SupportTask/relations/${relationId}`,
  SEARCH: `${API_PREFIX}/SupportTask/search`,
  REORDER: `${API_PREFIX}/SupportTask/reorder`,
};

// Certificate Definition Endpoints
export const CERTIFICATE_DEFINITION_ENDPOINTS = {
  LIST: `${API_PREFIX}/CertificateDefinition`,
  CREATE: `${API_PREFIX}/CertificateDefinition`,
  GET_BY_ID: (id) => `${API_PREFIX}/CertificateDefinition/${id}`,
  UPDATE: (id) => `${API_PREFIX}/CertificateDefinition/${id}`,
  DELETE: (id) => `${API_PREFIX}/CertificateDefinition/${id}`,
};

// User Certificate Endpoints
export const USER_CERTIFICATE_ENDPOINTS = {
  MY: `${API_PREFIX}/UserCertificate/my`,
  ADD_MY: `${API_PREFIX}/UserCertificate/my`,
  UPDATE_MY: (id) => `${API_PREFIX}/UserCertificate/my/${id}`,
  DELETE_MY: (id) => `${API_PREFIX}/UserCertificate/my/${id}`,
  USER: (userId) => `${API_PREFIX}/UserCertificate/user/${userId}`,
  WORK_ORDER_COMPLIANCE: (workOrderId) => `${API_PREFIX}/UserCertificate/work-order/${workOrderId}/compliance`,
  ORG_SUMMARY: `${API_PREFIX}/UserCertificate/org-summary`,
};

// Usage Endpoints
export const USAGE_ENDPOINTS = {
  MY_USAGE: `${API_PREFIX}/Usage/my-usage`,
  SUMMARY: `${API_PREFIX}/Usage/summary`,
  HISTORY: (orgId) => `${API_PREFIX}/Usage/history/${orgId}`,
  OVERAGES: (orgId) => `${API_PREFIX}/Usage/overages/${orgId}`,
  ALL_ORGANIZATIONS: `${API_PREFIX}/Usage/all-organizations`,
};

// Super Admin Endpoints
export const SUPER_ADMIN_ENDPOINTS = {
  DASHBOARD: `${API_PREFIX}/SuperAdmin/dashboard`,
  ORGANIZATIONS_OVERVIEW: `${API_PREFIX}/SuperAdmin/organizations/overview`,
  ORGANIZATION_SUMMARY: (orgId) => `${API_PREFIX}/SuperAdmin/organizations/${orgId}/summary`,
  ORGANIZATION_CUSTOMERS: (orgId) => `${API_PREFIX}/SuperAdmin/organizations/${orgId}/customers`,
};

// Work Order Report Endpoints
export const WORK_ORDER_REPORT_ENDPOINTS = {
  LIST: `${API_PREFIX}/WorkOrderReport`,
  FOR_WORK_ORDER: (workOrderId) => `${API_PREFIX}/WorkOrderReport/work-order/${workOrderId}`,
  GET_BY_ID: (id) => `${API_PREFIX}/WorkOrderReport/${id}`,
  CREATE_DRAFT: (workOrderId) => `${API_PREFIX}/WorkOrderReport/work-order/${workOrderId}/draft`,
  CONFIGURE: (id) => `${API_PREFIX}/WorkOrderReport/${id}/configure`,
  GENERATE: (id) => `${API_PREFIX}/WorkOrderReport/${id}/generate`,
  DOWNLOAD: (id) => `${API_PREFIX}/WorkOrderReport/${id}/download`,
  DELETE: (id) => `${API_PREFIX}/WorkOrderReport/${id}`,
  AVAILABLE_SECTIONS: (workOrderId) => `${API_PREFIX}/WorkOrderReport/work-order/${workOrderId}/available-sections`,
  ADD_ATTACHMENT: (workOrderId) => `${API_PREFIX}/WorkOrderReport/work-order/${workOrderId}/attachments`,
  LIST_ATTACHMENTS: (workOrderId) => `${API_PREFIX}/WorkOrderReport/work-order/${workOrderId}/attachments`,
  DELETE_ATTACHMENT: (attachmentId) => `${API_PREFIX}/WorkOrderReport/attachments/${attachmentId}`,
};

// Webhook Endpoints
export const WEBHOOK_ENDPOINTS = {
  LIST_ENDPOINTS: `${API_PREFIX}/Webhook/endpoints`,
  GET_ENDPOINT: (id) => `${API_PREFIX}/Webhook/endpoints/${id}`,
  CREATE_ENDPOINT: `${API_PREFIX}/Webhook/endpoints`,
  UPDATE_ENDPOINT: (id) => `${API_PREFIX}/Webhook/endpoints/${id}`,
  DELETE_ENDPOINT: (id) => `${API_PREFIX}/Webhook/endpoints/${id}`,
  TEST_ENDPOINT: (id) => `${API_PREFIX}/Webhook/endpoints/${id}/test`,
  REGENERATE_SECRET: (id) => `${API_PREFIX}/Webhook/endpoints/${id}/regenerate-secret`,
  ENABLE_ENDPOINT: (id) => `${API_PREFIX}/Webhook/endpoints/${id}/enable`,
  LIST_DELIVERIES: `${API_PREFIX}/Webhook/deliveries`,
  GET_DELIVERY: (id) => `${API_PREFIX}/Webhook/deliveries/${id}`,
  EVENT_TYPES: `${API_PREFIX}/Webhook/event-types`,
  SAMPLE_PAYLOAD: (eventType) => `${API_PREFIX}/Webhook/sample-payload/${eventType}`,
};

// Audit Log Endpoints
export const AUDIT_LOG_ENDPOINTS = {
  LIST: `${API_PREFIX}/AuditLog`,
  GET_BY_ID: (id) => `${API_PREFIX}/AuditLog/${id}`,
  ENTITY_HISTORY: (entityType, entityId) => `${API_PREFIX}/AuditLog/entity/${entityType}/${entityId}`,
  USER_ACTIVITY: (userId) => `${API_PREFIX}/AuditLog/user/${userId}`,
};

// Lab Report Endpoints
export const LAB_REPORT_ENDPOINTS = {
  LIST: `${API_PREFIX}/LabReport`,
  UPLOAD_URL: `${API_PREFIX}/LabReport/upload-url`,
  CONFIRM: `${API_PREFIX}/LabReport`,
  GET_BY_ID: (id) => `${API_PREFIX}/LabReport/${id}`,
  PDF_URL: (id) => `${API_PREFIX}/LabReport/${id}/pdf-url`,
  RAW_DATA: (id) => `${API_PREFIX}/LabReport/${id}/raw-data`,
  APPROVE: (id) => `${API_PREFIX}/LabReport/${id}/approve`,
  UPDATE_SAMPLE: (id, sampleId) => `${API_PREFIX}/LabReport/${id}/samples/${sampleId}`,
  UPDATE_RESULT: (id, resultId) => `${API_PREFIX}/LabReport/${id}/results/${resultId}`,
  ADD_RESULT: (id) => `${API_PREFIX}/LabReport/${id}/results`,
  DELETE_RESULT: (id, resultId) => `${API_PREFIX}/LabReport/${id}/results/${resultId}`,
  MATCH_WORK_ORDER: (id) => `${API_PREFIX}/LabReport/${id}/match-work-order`,
  SUGGEST_MATCHES: (id) => `${API_PREFIX}/LabReport/${id}/suggest-matches`,
  DELETE: (id) => `${API_PREFIX}/LabReport/${id}`,
  ATTEMPT_MATCH: (id) => `${API_PREFIX}/LabReport/${id}/attempt-match`,
  LINK_SUBMISSION: (id, submissionId) => `${API_PREFIX}/LabReport/${id}/link-submission/${submissionId}`,
  UNLINK_SUBMISSION: (id) => `${API_PREFIX}/LabReport/${id}/link-submission`,
  LINKED_SUBMISSION: (id) => `${API_PREFIX}/LabReport/${id}/linked-submission`,
  SUBMISSION_CANDIDATES: (id) => `${API_PREFIX}/LabReport/${id}/submission-candidates`,
  SAMPLE_LINK_SUBMISSION: (id, sampleId, submissionId) => `${API_PREFIX}/LabReport/${id}/samples/${sampleId}/link-submission/${submissionId}`,
  SAMPLE_UNLINK_SUBMISSION: (id, sampleId) => `${API_PREFIX}/LabReport/${id}/samples/${sampleId}/link-submission`,
  SAMPLE_CANDIDATES: (id, sampleId) => `${API_PREFIX}/LabReport/${id}/samples/${sampleId}/submission-candidates`,
  SAMPLE_LINKS: (id) => `${API_PREFIX}/LabReport/${id}/sample-links`,
  SUBMISSION_SAMPLE_LINKS: (submissionId) => `${API_PREFIX}/LabReport/submission/${submissionId}/sample-links`,
  SUBMISSION_ATTEMPT_MATCH: (submissionId) => `${API_PREFIX}/LabReport/submission/${submissionId}/attempt-match`,
  EVALUATE: `${API_PREFIX}/LabReport/evaluate`,
};

// Lab Report Template Endpoints
export const LAB_REPORT_TEMPLATE_ENDPOINTS = {
  LIST: `${API_PREFIX}/LabReportTemplate`,
  GET_BY_ID: (id) => `${API_PREFIX}/LabReportTemplate/${id}`,
  CREATE: `${API_PREFIX}/LabReportTemplate`,
  UPDATE: (id) => `${API_PREFIX}/LabReportTemplate/${id}`,
  DELETE: (id) => `${API_PREFIX}/LabReportTemplate/${id}`,
};

// Lab Document Format Endpoints
export const LAB_DOCUMENT_FORMAT_ENDPOINTS = {
  LIST: `${API_PREFIX}/LabDocumentFormat`,
  GET_BY_ID: (id) => `${API_PREFIX}/LabDocumentFormat/${id}`,
  CREATE: `${API_PREFIX}/LabDocumentFormat`,
  UPDATE: (id) => `${API_PREFIX}/LabDocumentFormat/${id}`,
  DELETE: (id) => `${API_PREFIX}/LabDocumentFormat/${id}`,
  UPLOAD_URL: `${API_PREFIX}/LabDocumentFormat/upload-url`,
  CALIBRATE: `${API_PREFIX}/LabDocumentFormat/calibrate`,
  DETECT: `${API_PREFIX}/LabDocumentFormat/detect`,
};

// AI Model Config Endpoints
export const AI_MODEL_ENDPOINTS = {
  LIST: `${API_PREFIX}/AIModelConfig`,
  UPDATE: (id) => `${API_PREFIX}/AIModelConfig/${id}`,
  AVAILABLE_MODELS: `${API_PREFIX}/AIModelConfig/available-models`,
  TEST: `${API_PREFIX}/AIModelConfig/test`,
};

// AI Usage Log Endpoints
export const AI_USAGE_ENDPOINTS = {
  LIST: `${API_PREFIX}/AIUsageLog`,
  SUMMARY: `${API_PREFIX}/AIUsageLog/summary`,
};

// Inbound Webhook Endpoints
export const INBOUND_WEBHOOK_ENDPOINTS = {
  LIST_ENDPOINTS: `${API_PREFIX}/webhook/inbound/endpoints`,
  GET_ENDPOINT: (id) => `${API_PREFIX}/webhook/inbound/endpoints/${id}`,
  CREATE_ENDPOINT: `${API_PREFIX}/webhook/inbound/endpoints`,
  UPDATE_ENDPOINT: (id) => `${API_PREFIX}/webhook/inbound/endpoints/${id}`,
  DELETE_ENDPOINT: (id) => `${API_PREFIX}/webhook/inbound/endpoints/${id}`,
  TEST_ENDPOINT: (id) => `${API_PREFIX}/webhook/inbound/endpoints/${id}/test`,
  REGENERATE_TOKEN: (id) => `${API_PREFIX}/webhook/inbound/endpoints/${id}/regenerate-token`,
  LIST_DELIVERIES: `${API_PREFIX}/webhook/inbound/deliveries`,
  GET_DELIVERY: (id) => `${API_PREFIX}/webhook/inbound/deliveries/${id}`,
};

// Database Backup Endpoints
export const DATABASE_BACKUP_ENDPOINTS = {
  STATUS: `${API_PREFIX}/DatabaseBackup/status`,
  FILES: `${API_PREFIX}/DatabaseBackup/files`,
  DOWNLOAD: `${API_PREFIX}/DatabaseBackup/files/download`,
  RESTORE: `${API_PREFIX}/DatabaseBackup/restore`,
};

/**
 * Build query string from params object
 * @param {Object} params - Query parameters
 * @returns {string} Query string
 */
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};
